import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ClickEventComponentNames } from 'features/analytics';
import { ProductCustomizationDrinksTabs } from 'features/menu/components/product-detail/product-customization/product-customization-drinks-tabs';
import { ProductCustomizationModifiers } from 'features/menu/components/product-detail/product-customization/product-customization-modifiers';
import { DrinksTabsContent } from 'features/menu/components/product-detail/product-customization/styled';
import {
  CATEGORY_ANALYTICS_NAME,
  CustomizationRenderItem,
  DrinkCategory,
} from 'features/menu/components/product-detail/product-customization/types';
import {
  getComboSlotItems,
  getDrinkCategory,
} from 'features/menu/components/product-detail/product-customization/utils';
import {
  FADE_IN_ANIMATION_MILLIS,
  SLIDE_IN_ANIMATION_MILLIS,
  SelectorContainer,
} from 'features/menu/components/product-detail/styled';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { useStoreContext } from 'features/store';
import Animated, {
  FadeIn,
  LayoutAnimationConfig,
  SlideInLeft,
  SlideInRight,
} from 'react-native-reanimated';

import { ListCardGroup } from 'components/list-card-group';
import { useSelectedGridRow } from 'components/list-card-group/use-selected-grid-row';
import { ProductCard } from 'components/product-card';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { useMainMenuContext } from 'state/menu/main-menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getEntryPrice, stringifySanityImage } from 'state/unified-menu/utils';

export const ProductCustomizationDrinks = () => {
  const { indexedMenu } = useUnifiedMenuContext();
  const { currentFlowStep } = useProductWizardFlow();
  const { slotsSelections, selectComboSlotItem } = useProductWizardContext();
  const menuQueryOptions = useMenuQueryOptions();
  const { formatCalories } = useMenuCalories();

  const [isMounted, setIsMounted] = useState<boolean>(false);
  const previousTabIndexRef = useRef<number>(0);

  const slotKey = currentFlowStep.slotKey!;
  const selectedSlot = slotsSelections[slotKey];

  const { store } = useStoreContext();
  const { activeDayParts } = useMainMenuContext();
  const storeHasBurgersForBreakfast = Boolean(store.hasBurgersForBreakfast);

  const cachedComboDrinks: ILocalizedMenuEntry[] = useMemo(
    () =>
      getComboSlotItems({
        menu: indexedMenu,
        slotKey,
        posServiceMode: menuQueryOptions.posServiceMode,
        activeDayParts,
        storeHasBurgersForBreakfast,
      }),
    [
      indexedMenu,
      slotKey,
      menuQueryOptions.posServiceMode,
      activeDayParts,
      storeHasBurgersForBreakfast,
    ]
  );

  const availableCategories: DrinkCategory[] = useMemo(
    () =>
      cachedComboDrinks
        .reduce((categories, drink) => {
          const drinkCategory = getDrinkCategory(drink);
          if (!categories.includes(drinkCategory)) {
            categories.push(drinkCategory);
          }
          return categories;
        }, [] as DrinkCategory[])
        .sort(),
    [cachedComboDrinks]
  );

  const [activeTab, setActiveTab] = useState<DrinkCategory>(() => {
    const comboSlot = indexedMenu.find(slotKey);

    const selectedDrink =
      cachedComboDrinks.find(({ id }) => id === selectedSlot?.itemId) ||
      (comboSlot?.options?.defaults?.[0]
        ? indexedMenu.find(comboSlot.options.defaults[0])
        : undefined);

    return selectedDrink ? getDrinkCategory(selectedDrink) : availableCategories[0];
  });

  const drinksForActiveTab = useMemo(
    () => cachedComboDrinks.filter(drink => getDrinkCategory(drink) === activeTab),
    [cachedComboDrinks, activeTab]
  );

  const { selectedRow, handleItemPress } = useSelectedGridRow({
    items: drinksForActiveTab,
    selectedItemId: selectedSlot?.itemId,
  });

  const enteringAnimation = useMemo(() => {
    if (!isMounted) {
      return;
    }

    const previousTabIndex = previousTabIndexRef.current;

    // Determine if we are moving forward or backward
    if (activeTab > previousTabIndex) {
      return SlideInRight;
    } else if (activeTab < previousTabIndex) {
      return SlideInLeft;
    }

    // Default animation if we are at the same tab (no change)
    return SlideInRight;
  }, [activeTab, isMounted]);

  const handleDrinksTabChange = (tab: DrinkCategory) => {
    // Update the previous tab index ref before changing the state
    previousTabIndexRef.current = activeTab;
    setActiveTab(tab);

    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.PRODUCT_DRINKS_CATEGORY_VIEW,
        name: CATEGORY_ANALYTICS_NAME[tab],
      },
    });
  };

  const selectedDrinkCustomization = useMemo(() => {
    if (!selectedRow) {
      return null;
    }

    const selectedItem = cachedComboDrinks.find(item => item.id === selectedRow.itemId);

    if (!selectedItem?.options?.entries?.length) {
      return null;
    }

    return <ProductCustomizationModifiers />;
  }, [selectedRow, cachedComboDrinks]);

  // LayoutAnimationConfig skipEntering logic for web devices
  useLayoutEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <SelectorContainer>
      <ProductCustomizationDrinksTabs
        activeTab={activeTab}
        categories={availableCategories}
        onTabPress={activeTab => {
          handleDrinksTabChange(activeTab);
        }}
      />

      <DrinksTabsContent>
        <LayoutAnimationConfig skipEntering>
          <Animated.View
            key={`animation-${activeTab}`}
            entering={FadeIn.duration(FADE_IN_ANIMATION_MILLIS)}
          >
            <Animated.View entering={enteringAnimation?.duration(SLIDE_IN_ANIMATION_MILLIS)}>
              <ListCardGroup
                items={drinksForActiveTab}
                key={`drink-list-${activeTab}`}
                selectedRow={selectedRow?.rowIndex}
                ItemCustomizationComponent={selectedDrinkCustomization}
                renderItem={({ item, index, rowIndex }: CustomizationRenderItem) => (
                  <ProductCard
                    key={`drink-${item.id}-${index}`}
                    isSelected={item.id === selectedSlot?.itemId}
                    productProps={{
                      name: item.name?.locale,
                      price: getEntryPrice(item, menuQueryOptions.posServiceMode),
                      calories: formatCalories(item.nutrition?.calories?.def ?? 0),
                      image: stringifySanityImage(item.image),
                    }}
                    onProductPress={() => {
                      logButtonClick({
                        attributes: {
                          component: ClickEventComponentNames.PRODUCT_COMBO_SLOT_SELECTION_UPDATE,
                          name: item.name?.locale ?? '',
                          Id: item.id ?? '',
                        },
                      });
                      selectComboSlotItem(slotKey, item.id);
                      handleItemPress(item, rowIndex);
                    }}
                  />
                )}
              />
            </Animated.View>
          </Animated.View>
        </LayoutAnimationConfig>
      </DrinksTabsContent>
    </SelectorContainer>
  );
};
